import axiosInstance from "core/api/axiosInstance";

// ==================== FUNCIONES DE UTILIDAD ====================

const getAuthInfo = () => {
  try {
    const token = localStorage.getItem("token");
    const user = localStorage.getItem("user");

    return {
      hasToken: !!token,
      token: token || null,
      tokenLength: token ? token.length : 0,
      hasUser: !!user,
      userData: user ? JSON.parse(user) : null,
    };
  } catch (error) {
    console.error("Error obteniendo información de autenticación:", error);
    return {
      hasToken: false,
      token: null,
      tokenLength: 0,
      hasUser: false,
      userData: null,
    };
  }
};

const handleServiceError = (error, serviceName) => {
  console.error(`❌ Error en ${serviceName}:`, error);

  let errorMessage = `Error en ${serviceName}`;
  let errorDetails = {};

  if (error.response) {
    const backendMessage = error.response.data?.message || error.response.data?.msm || "";
    const backendError = error.response.data?.error || error.response.data?.data?.error || "";

    errorMessage =
      backendError ||
      backendMessage ||
      `Error ${error.response.status}: ${error.response.statusText || "Error desconocido"}`;

    errorDetails = {
      status: error.response.status,
      statusText: error.response.statusText,
      data: error.response.data,
      headers: error.response.headers,
    };

    if (error.response.status === 401) {
      errorMessage =
        backendMessage || "No autorizado. Su sesión ha expirado o el token es inválido.";
    } else if (error.response.status === 403) {
      errorMessage =
        backendMessage || "Acceso denegado. No tiene permisos para realizar esta acción.";
    } else if (error.response.status === 404) {
      errorMessage = "Endpoint no encontrado.";
    } else if (error.response.status >= 500) {
      errorMessage = backendMessage || "Error interno del servidor.";
    }
  } else if (error.request) {
    errorMessage =
      "La solicitud está tomando más tiempo de lo esperado. El procesamiento podría continuar en segundo plano.";
    errorDetails = { request: error.request, isTimeout: true };
  } else {
    errorMessage = error.message || "Error de configuración de la solicitud";
    errorDetails = { message: error.message, stack: error.stack };
  }

  return {
    error: true,
    message: errorMessage,
    details: errorDetails,
    shouldLogout: false,
    isTimeout: error.code === "ECONNABORTED" || error.message?.includes("timeout"),
  };
};

/**
 * 🔑 Extrae el error real del payload del backend.
 * El backend a veces devuelve { data: { error: "..." }, msm: "succesful" }
 * donde `msm` no refleja el error real.
 */
const extractBackendError = (payload) => {
  if (!payload) return null;

  // Buscar error en distintas formas
  const possibleErrors = [
    payload?.data?.error,
    payload?.error,
    payload?.data?.message_error,
    payload?.message_error,
  ].filter(Boolean);

  if (possibleErrors.length > 0) {
    return possibleErrors[0];
  }

  // Si hay validationErrors / errores array con contenido
  const errArray =
    payload?.data?.validationErrors || payload?.data?.errores || payload?.validationErrors;

  if (Array.isArray(errArray) && errArray.length > 0) {
    return errArray[0];
  }

  return null;
};

/**
 * 🔑 Normaliza la respuesta del backend a la estructura que espera el frontend.
 */
const normalizeImportResponse = (payload, httpStatus) => {
  const backendError = extractBackendError(payload);

  // ❗ Si hay error explícito en el payload, marcar como error SIN importar el msm/status
  if (backendError) {
    return {
      success: false,
      error: backendError,
      importedCount: 0,
      errorsCount: 0,
      totalRows: 0,
      validationErrors: [],
      validationSummary: {
        totalRows: 0,
        validRows: 0,
        invalidRows: 0,
      },
      raw: payload,
      responseStatus: httpStatus,
      timestamp: new Date().toISOString(),
    };
  }

  // ✅ Caso exitoso → buscar contadores con múltiples nombres posibles
  const resultData = payload?.data || {};

  const importedCount =
    resultData.importedCount ??
    resultData.insertados ??
    resultData.processed ??
    resultData.saved ??
    resultData.importados ??
    0;

  const errorsCount =
    resultData.errorsCount ?? resultData.errores ?? resultData.failed ?? resultData.rechazados ?? 0;

  const totalRows =
    resultData.totalRows ??
    resultData.total ??
    resultData.totalRegistros ??
    importedCount + errorsCount;

  const validationErrors =
    resultData.validationErrors || resultData.errores_detalle || resultData.errors || [];

  return {
    success: true,
    importedCount,
    errorsCount,
    totalRows,
    message:
      payload?.msm === "succesful" || payload?.msm === "successful"
        ? payload?.message || "Importación completada exitosamente"
        : payload?.msm || payload?.message || "Importación completada exitosamente",
    data: resultData,
    validationErrors,
    duplicateInFile: resultData.duplicateInFile || [],
    duplicateInDatabase: resultData.duplicateInDatabase || [],
    validationSummary: {
      totalRows,
      validRows: resultData.validCount ?? importedCount,
      invalidRows: resultData.invalidCount ?? errorsCount,
      duplicateInFile: resultData.duplicateInFile?.length || 0,
      duplicateInDatabase: resultData.duplicateInDatabase?.length || 0,
    },
    raw: payload,
    responseStatus: httpStatus,
    timestamp: new Date().toISOString(),
  };
};

// ==================== CONSTANTES DE CONFIGURACIÓN ====================

const CONFIG = {
  MAX_FILE_SIZE: 200 * 1024 * 1024,
  UPLOAD_TIMEOUT: 4000000,
  POLLING_INTERVAL: 2000,
  MAX_POLLING_ATTEMPTS: 3600,
  BATCH_SIZE: 1000,
  VALID_FILE_TYPES: [
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.ms-excel",
  ],
};

// ==================== SERVICIO PARA OBTENER ARCHIVOS POR LLAVE ====================

export const getConciliationFiles = async (key) => {
  try {
    if (!key) {
      throw new Error("La llave de conciliación es requerida");
    }

    const authInfo = getAuthInfo();
    if (!authInfo.hasToken) {
      throw new Error("No hay token de autenticación. Por favor inicie sesión.");
    }

    const config = {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${authInfo.token}`,
      },
      timeout: 30000,
    };

    const response = await axiosInstance.get(
      `/api/conciliation/get-conciliations-files/${key}`,
      config
    );

    if (!response.data) {
      throw new Error("No se recibió respuesta del servidor");
    }

    if (response.data.code !== undefined && response.data.code !== 0) {
      throw new Error(response.data.message || `Error del servidor: código ${response.data.code}`);
    }

    return {
      success: true,
      data: response.data.data || [],
      count: response.data.count || 0,
      message: response.data.message || "Archivos obtenidos exitosamente",
      code: response.data.code,
      timestamp: new Date().toISOString(),
    };
  } catch (error) {
    console.error(`❌ Error en getConciliationFiles para llave ${key}:`, {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
    });

    const errorResult = handleServiceError(error, "getConciliationFiles");
    return {
      success: false,
      data: [],
      count: 0,
      error: errorResult.message,
      details: errorResult.details,
      shouldLogout: false,
      timestamp: new Date().toISOString(),
    };
  }
};

// ==================== SERVICIO PARA IMPORTAR ARCHIVO DE LOTES (SINCRÓNICO) ====================

export const importLoteFile = async (file, lote = null, onProgress = null) => {
  try {
    const authInfo = getAuthInfo();
    if (!authInfo.hasToken) {
      throw new Error("No hay token de autenticación. Por favor inicie sesión.");
    }

    if (!file) {
      throw new Error("Debe seleccionar un archivo para importar.");
    }

    if (!CONFIG.VALID_FILE_TYPES.includes(file.type) && !file.name.match(/\.(xlsx|xls)$/i)) {
      throw new Error(
        "Formato de archivo no válido. Solo se permiten archivos Excel (.xlsx, .xls)"
      );
    }

    if (file.size > CONFIG.MAX_FILE_SIZE) {
      throw new Error(`El archivo es demasiado grande. Tamaño máximo: 100MB`);
    }

    const formData = new FormData();
    formData.append("file", file);
    if (lote) {
      formData.append("lote", lote);
    }

    const config = {
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${authInfo.token}`,
      },
      timeout: CONFIG.UPLOAD_TIMEOUT,
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percentCompleted);
        }
      },
    };

    const response = await axiosInstance.post("/api/conciliation/import", formData, config);
    const { data, status } = response;

    if (!data) {
      throw new Error("No se recibió respuesta del servidor.");
    }

    // 🔑 Normalizar la respuesta (detecta errores aunque `msm` diga "succesful")
    const normalized = normalizeImportResponse(data, status);

    return normalized;
  } catch (error) {
    console.error("❌ Error en importLoteFile:", error);
    const errorResult = handleServiceError(error, "importLoteFile");

    if (errorResult.isTimeout) {
      return {
        success: false,
        error: errorResult.message,
        isTimeout: true,
        mightHaveProcessed: true,
        details: errorResult.details,
        timestamp: new Date().toISOString(),
      };
    }

    return {
      success: false,
      error: errorResult.message,
      details: errorResult.details,
      shouldLogout: false,
      timestamp: new Date().toISOString(),
    };
  }
};

// ==================== SERVICIO PARA IMPORTAR ARCHIVO DE LOTES (ASÍNCRONO) ====================

export const importLoteFileAsync = async (file, lote = null, onUploadProgress = null) => {
  try {
    const authInfo = getAuthInfo();
    if (!authInfo.hasToken) {
      throw new Error("No hay token de autenticación. Por favor inicie sesión.");
    }

    if (!file) {
      throw new Error("Debe seleccionar un archivo para importar.");
    }

    if (!CONFIG.VALID_FILE_TYPES.includes(file.type) && !file.name.match(/\.(xlsx|xls)$/i)) {
      throw new Error(
        "Formato de archivo no válido. Solo se permiten archivos Excel (.xlsx, .xls)"
      );
    }

    const maxSize = 500 * 1024 * 1024;
    if (file.size > maxSize) {
      throw new Error(`El archivo es demasiado grande. Tamaño máximo: 500MB`);
    }

    const formData = new FormData();
    formData.append("file", file);
    if (lote) {
      formData.append("lote", lote);
    }
    formData.append("async", "true");
    formData.append("batchSize", CONFIG.BATCH_SIZE.toString());

    const config = {
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${authInfo.token}`,
      },
      timeout: 120000,
      onUploadProgress: (progressEvent) => {
        if (onUploadProgress && progressEvent.total) {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onUploadProgress(percentCompleted);
        }
      },
    };

    const response = await axiosInstance.post("/api/conciliation/import-async", formData, config);
    const { data, status } = response;

    if (!data) {
      throw new Error("No se recibió respuesta del servidor.");
    }

    // 🔑 Detectar error aunque venga con msm: succesful
    const backendError = extractBackendError(data);
    if (backendError) {
      return {
        success: false,
        error: backendError,
        details: data,
        timestamp: new Date().toISOString(),
      };
    }

    return {
      success: true,
      jobId: data.jobId || data.id || data.processId || data.data?.jobId,
      message: data.message || "Importación asíncrona iniciada exitosamente",
      status: data.status || "processing",
      estimatedTime: data.estimatedTime || null,
      totalBatches: data.totalBatches || 0,
      timestamp: new Date().toISOString(),
      responseStatus: status,
    };
  } catch (error) {
    console.error("❌ Error en importLoteFileAsync:", error);
    const errorResult = handleServiceError(error, "importLoteFileAsync");
    return {
      success: false,
      error: errorResult.message,
      details: errorResult.details,
      isTimeout: errorResult.isTimeout,
      timestamp: new Date().toISOString(),
    };
  }
};

// ==================== SERVICIO PARA VERIFICAR ESTADO DE IMPORTACIÓN ====================

export const checkImportStatus = async (jobId) => {
  try {
    if (!jobId) {
      throw new Error("El ID del trabajo es requerido");
    }

    const authInfo = getAuthInfo();
    if (!authInfo.hasToken) {
      throw new Error("No hay token de autenticación.");
    }

    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authInfo.token}`,
      },
      timeout: 15000,
    };

    const response = await axiosInstance.get(`/api/conciliation/import-status/${jobId}`, config);
    const { data, status } = response;

    if (!data) {
      throw new Error("No se recibió respuesta del servidor.");
    }

    const backendError = extractBackendError(data);
    if (backendError) {
      return {
        success: false,
        jobId,
        status: "error",
        error: backendError,
        details: data,
        timestamp: new Date().toISOString(),
      };
    }

    const resultData = data.data || data;

    return {
      success: true,
      jobId,
      status: resultData.status || "unknown",
      progress: resultData.progress || 0,
      totalRows: resultData.totalRows || 0,
      processedRows: resultData.processedRows || 0,
      importedCount: resultData.importedCount || 0,
      errorsCount: resultData.errorsCount || 0,
      batchProcessed: resultData.batchProcessed || 0,
      totalBatches: resultData.totalBatches || 0,
      message: resultData.message || "",
      errorDetails: resultData.errorDetails || null,
      estimatedTimeRemaining: resultData.estimatedTimeRemaining || null,
      startTime: resultData.startTime || null,
      endTime: resultData.endTime || null,
      timestamp: new Date().toISOString(),
      responseStatus: status,
    };
  } catch (error) {
    console.error(`❌ Error en checkImportStatus para job ${jobId}:`, error);
    const errorResult = handleServiceError(error, "checkImportStatus");
    return {
      success: false,
      jobId,
      status: "error",
      error: errorResult.message,
      details: errorResult.details,
      timestamp: new Date().toISOString(),
    };
  }
};

// ==================== SERVICIO PARA POLLING DE IMPORTACIÓN ====================

export const pollImportStatus = (jobId, onProgress, onComplete, onError, options = {}) => {
  const {
    interval = CONFIG.POLLING_INTERVAL,
    maxAttempts = CONFIG.MAX_POLLING_ATTEMPTS,
    onStatusChange = null,
  } = options;

  let attempts = 0;
  let previousStatus = "";
  let cancelled = false;
  let timeoutId = null;

  const poll = async () => {
    if (cancelled) return;

    try {
      attempts++;
      const result = await checkImportStatus(jobId);
      if (cancelled) return;

      if (!result.success) {
        if (onError) onError(result.error || "Error al verificar el estado");
        return;
      }

      if (onStatusChange && result.status !== previousStatus) {
        onStatusChange(result.status, result);
        previousStatus = result.status;
      }

      if (onProgress) {
        onProgress(result.progress || 0, result.processedRows || 0, result.totalRows || 0, {
          batch: result.batchProcessed || 0,
          totalBatches: result.totalBatches || 0,
          imported: result.importedCount || 0,
          errors: result.errorsCount || 0,
          estimatedTimeRemaining: result.estimatedTimeRemaining || null,
        });
      }

      if (result.status === "completed" || result.status === "success") {
        if (onComplete) {
          onComplete({
            ...result,
            finalMessage: "Importación completada exitosamente",
          });
        }
        return;
      } else if (result.status === "failed" || result.status === "error") {
        if (onError) {
          onError(
            result.errorDetails || result.error || result.message || "Error en el procesamiento"
          );
        }
        return;
      }

      if (attempts < maxAttempts) {
        timeoutId = setTimeout(poll, interval);
      } else {
        if (onError) {
          onError(
            `Tiempo de espera agotado (${maxAttempts} intentos). La importación podría estar todavía en proceso.`
          );
        }
      }
    } catch (error) {
      if (cancelled) return;
      console.error("Error en polling:", error);
      if (onError) {
        onError(error.message || "Error en el monitoreo de la importación");
      }
    }
  };

  poll();

  return {
    cancel: () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    },
  };
};

// ==================== SERVICIO PARA OBTENER LLAVES DE CONCILIACIÓN ====================

export const getConciliationKeys = async () => {
  try {
    const authInfo = getAuthInfo();
    if (!authInfo.hasToken) {
      throw new Error("No hay token de autenticación. Por favor inicie sesión.");
    }

    const config = {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${authInfo.token}`,
      },
      timeout: 15000,
    };

    const response = await axiosInstance.get("/api/conciliation/get-conciliation-keys", config);
    const { data, status } = response;

    if (!data) {
      throw new Error("No se recibió respuesta del servidor.");
    }

    const isSuccess = status >= 200 && status < 300;

    if (!isSuccess) {
      throw new Error(data.msm || data.message || "Error al obtener las llaves de conciliación");
    }

    const keys = data.data || [];
    const validKeys = keys.filter((item) => item.key_concil !== null);

    return {
      success: true,
      data: validKeys,
      count: validKeys.length,
      total: data.count || validKeys.length,
      message: data.message || "Llaves de conciliación obtenidas exitosamente",
      timestamp: new Date().toISOString(),
      responseStatus: status,
    };
  } catch (error) {
    console.error("❌ Error en getConciliationKeys:", error);
    const errorResult = handleServiceError(error, "getConciliationKeys");
    return {
      success: false,
      data: [],
      count: 0,
      total: 0,
      error: errorResult.message,
      details: errorResult.details,
      shouldLogout: false,
      timestamp: new Date().toISOString(),
    };
  }
};

// ==================== SERVICIO PARA OBTENER DATOS POR LLAVE ====================

export const getConciliationDataByKey = async (key) => {
  try {
    if (!key) {
      throw new Error("La llave de conciliación es requerida");
    }

    const authInfo = getAuthInfo();
    if (!authInfo.hasToken) {
      throw new Error("No hay token de autenticación. Por favor inicie sesión.");
    }

    const config = {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${authInfo.token}`,
      },
      timeout: 30000,
    };

    const response = await axiosInstance.get(
      `/api/conciliation/get-conciliations-keys-data/${key}`,
      config
    );
    const { data, status } = response;

    if (!data) {
      throw new Error("No se recibió respuesta del servidor.");
    }

    const isSuccess = status >= 200 && status < 300;

    if (!isSuccess) {
      throw new Error(data.msm || data.message || "Error al obtener los datos de conciliación");
    }

    const resultData = data.data || [];

    // ============ VERIFICAR ARCHIVOS DE PAGO ============
    let hasPaymentFiles = false;
    let paymentFiles = [];

    try {
      const filesResult = await getConciliationFiles(key);
      if (filesResult.success && filesResult.data && filesResult.data.length > 0) {
        hasPaymentFiles = true;
        paymentFiles = filesResult.data;
      }
    } catch (error) {
      console.error(`Error verificando archivos de pago para llave ${key}:`, error);
    }
    // ====================================================

    const mappedData = resultData.map((item) => ({
      id: item.id,
      name: item.name || "N/A",
      observation: item.observation || "N/A",
      status_concil: item.status_concil,
      messages: item.messages || [],
      files: item.files || null,
      key_concil: item.key_concil || "N/A",
      conciliation_date: item.conciliation_date || "N/A",
      vertical: item.vertical || "N/A",
      status: item.status,
      idcommerce: item.idcommerce,
      created_at: item.created_at,
      updated_at: item.updated_at,
      filename: item.files?.originalname || null,
      has_payment_files: hasPaymentFiles,
      payment_files_count: paymentFiles.length,
      payment_files: paymentFiles,
    }));

    return {
      success: true,
      data: mappedData,
      count: resultData.length,
      total: data.count || resultData.length,
      message: data.message || "Datos de conciliación obtenidos exitosamente",
      hasPaymentFiles: hasPaymentFiles,
      paymentFilesCount: paymentFiles.length,
      timestamp: new Date().toISOString(),
      responseStatus: status,
    };
  } catch (error) {
    console.error(`❌ Error en getConciliationDataByKey para llave ${key}:`, error);
    const errorResult = handleServiceError(error, "getConciliationDataByKey");
    return {
      success: false,
      data: [],
      count: 0,
      total: 0,
      error: errorResult.message,
      details: errorResult.details,
      shouldLogout: false,
      timestamp: new Date().toISOString(),
    };
  }
};

// ==================== EXPORTACIÓN POR DEFECTO ====================

export default {
  importLoteFile,
  importLoteFileAsync,
  checkImportStatus,
  pollImportStatus,
  getConciliationKeys,
  getConciliationDataByKey,
  getConciliationFiles,
  CONFIG,
};
